import React, { useState, useEffect } from "react";
import SystemAdminLayout from "../layouts/SystemAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { PILLAR_NAME_LIST } from '../data/pillars';
import { 
  Users, Search, CheckCircle, XCircle, Clock, 
  Eye, Award, Shield, Star, TrendingUp, Building2,
  Mail, Phone, Globe, MapPin, Calendar,
  Copy, Check, AlertCircle, Plus, X, Loader2, Briefcase,
  Share2, Printer, Send, ToggleLeft, ToggleRight, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_CONFIG = {
  registered: { name: "Registered", color: "#6B7280", bgColor: "#F3F4F6", description: "Entry tier with basic access" },
  authorized: { name: "Authorized", color: "#3B82F6", bgColor: "#DBEAFE", description: "Verified partner with expanded permissions" },
  strategic: { name: "Strategic", color: "#F59E0B", bgColor: "#FEF3C7", description: "Premium partner with maximum benefits" }
};

const STATUS_CONFIG = {
  pending: { name: "Pending Review", color: "#F59E0B", bgColor: "#FEF3C7" },
  approved: { name: "Approved", color: "#10B981", bgColor: "#D1FAE5" },
  rejected: { name: "Rejected", color: "#EF4444", bgColor: "#FEE2E2" }
};

const PartnerManagement = () => {
  const [partners, setPartners] = useState([]);
  const [counts, setCounts] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null);
  const [selectedTier, setSelectedTier] = useState('registered');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [lastApprovedCredentials, setLastApprovedCredentials] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [credentialsCopied, setCredentialsCopied] = useState(false);
  const [showEditCredentials, setShowEditCredentials] = useState(false);
  const [editCredentialsData, setEditCredentialsData] = useState({ username: '', password: '' });
  const [masterData, setMasterData] = useState({
    industries: [],
    organization_types: [],
    pillars: [],
    partnership_types: [],
    organization_sizes: [],
    geographic_reach: []
  });

  useEffect(() => {
    fetchPartners();
    fetchMasterData();
  }, [statusFilter]);

  const fetchMasterData = async () => {
    try {
      const response = await fetch(`${API}/partners/master-data`);
      const data = await response.json();
      setMasterData(data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchPartners = async () => {
    setLoading(true);
    try {
      let url = `${API}/partners/admin/applications`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      setPartners(data.partners || []);
      setCounts(data.counts || { total: 0, pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedPartner) return;
    setProcessing(true);

    try {
      const response = await fetch(`${API}/partners/admin/applications/${selectedPartner.partner_id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'approved',
          tier: selectedTier
        })
      });

      const result = await response.json();

      if (response.ok) {
        setLastApprovedCredentials(result.credentials);
        fetchPartners();
        setShowApprovalModal(false);
        setSelectedPartner(null);
      } else {
        alert(result.detail || 'Failed to approve partner');
      }
    } catch (error) {
      alert('Error approving partner');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedPartner) return;
    setProcessing(true);

    try {
      const response = await fetch(`${API}/partners/admin/applications/${selectedPartner.partner_id}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'rejected',
          rejection_reason: rejectionReason
        })
      });

      if (response.ok) {
        fetchPartners();
        setShowApprovalModal(false);
        setSelectedPartner(null);
        setRejectionReason('');
      } else {
        const result = await response.json();
        alert(result.detail || 'Failed to reject partner');
      }
    } catch (error) {
      alert('Error rejecting partner');
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleStatus = async (partner) => {
    try {
      const response = await fetch(`${API}/partners/admin/applications/${partner.partner_id}/toggle-status`, {
        method: 'PUT'
      });
      if (response.ok) {
        fetchPartners();
      }
    } catch (error) {
      alert('Error toggling partner status');
    }
  };

  const handleEditCredentials = async () => {
    if (!selectedPartner) return;
    setProcessing(true);

    try {
      const payload = {};
      if (editCredentialsData.username) payload.username = editCredentialsData.username;
      if (editCredentialsData.password) payload.password = editCredentialsData.password;

      const response = await fetch(`${API}/partners/admin/applications/${selectedPartner.partner_id}/credentials`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        alert('Credentials updated successfully!');
        setShowEditCredentials(false);
        setEditCredentialsData({ username: '', password: '' });
        fetchPartners();
        // Update selected partner with new credentials
        setSelectedPartner(prev => ({
          ...prev,
          credentials: result.credentials
        }));
      } else {
        alert(result.detail || 'Failed to update credentials');
      }
    } catch (error) {
      alert('Error updating credentials');
    } finally {
      setProcessing(false);
    }
  };

  const copyCredentials = (text) => {
    navigator.clipboard.writeText(text);
    setCredentialsCopied(true);
    setTimeout(() => setCredentialsCopied(false), 2000);
  };

  const handleSharePartner = (partner, method) => {
    const partnerInfo = `
Partner: ${partner.organization_name || partner.contact_name}
Type: ${partner.partnership_type || partner.partner_type}
Email: ${partner.email}
Phone: ${partner.phone || 'N/A'}
Location: ${partner.geographic_reach || 'N/A'}
Industry: ${partner.industry_focus || 'N/A'}
    `.trim();

    if (method === 'copy') {
      navigator.clipboard.writeText(partnerInfo);
      alert('Partner info copied to clipboard!');
    } else if (method === 'email') {
      window.open(`mailto:?subject=CNESS Partner Information&body=${encodeURIComponent(partnerInfo)}`);
    } else if (method === 'print') {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html><head><title>Partner Information</title></head>
        <body style="font-family: Arial, sans-serif; padding: 20px;">
          <h1>CNESS Partner Information</h1>
          <pre style="white-space: pre-wrap;">${partnerInfo}</pre>
        </body></html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const filteredPartners = partners.filter(partner => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      partner.organization_name?.toLowerCase().includes(search) ||
      partner.contact_name?.toLowerCase().includes(search) ||
      partner.email?.toLowerCase().includes(search) ||
      partner.partner_id?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  // Stats Cards
  const StatsCards = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {[
        { label: 'Total Partners', value: counts.total, icon: Users, color: 'from-blue-500 to-blue-600' },
        { label: 'Pending Review', value: counts.pending, icon: Clock, color: 'from-yellow-500 to-orange-500' },
        { label: 'Approved', value: counts.approved, icon: CheckCircle, color: 'from-green-500 to-emerald-500' },
        { label: 'Rejected', value: counts.rejected, icon: XCircle, color: 'from-red-500 to-rose-500' }
      ].map((stat, i) => (
        <Card key={i} className="border-0 shadow-md overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // Partner Detail Modal with Share Options
  const PartnerDetailModal = () => {
    if (!selectedPartner) return null;
    const statusConfig = STATUS_CONFIG[selectedPartner.status] || {};
    const tierConfig = TIER_CONFIG[selectedPartner.tier] || {};

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedPartner(null)} />
        
        <div className="relative min-h-screen flex items-center justify-center p-4">
          <Card className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-900 text-white sticky top-0 z-10">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-xl mb-1">
                    {selectedPartner.organization_name || selectedPartner.contact_name}
                  </CardTitle>
                  <p className="text-white/70 text-sm">{selectedPartner.partner_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-medium"
                    style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
                  >
                    {statusConfig.name}
                  </span>
                  {selectedPartner.tier && (
                    <span 
                      className="px-3 py-1 rounded-full text-sm font-medium"
                      style={{ backgroundColor: tierConfig.bgColor, color: tierConfig.color }}
                    >
                      {tierConfig.name}
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-6">
              {/* Share Buttons */}
              <div className="flex justify-end gap-2 mb-4 pb-4 border-b">
                <Button variant="outline" size="sm" onClick={() => handleSharePartner(selectedPartner, 'copy')}>
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleSharePartner(selectedPartner, 'email')}>
                  <Send className="w-4 h-4 mr-1" /> Email
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleSharePartner(selectedPartner, 'print')}>
                  <Printer className="w-4 h-4 mr-1" /> Print
                </Button>
              </div>

              {/* Organization Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-500" /> Organization
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Name:</span> <strong>{selectedPartner.organization_name}</strong></p>
                    <p><span className="text-gray-500">Type:</span> {selectedPartner.organization_type}</p>
                    <p><span className="text-gray-500">Size:</span> {selectedPartner.organization_size}</p>
                    <p><span className="text-gray-500">Industry:</span> {selectedPartner.industry_focus}</p>
                    {selectedPartner.website && (
                      <p className="flex items-center gap-1">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <a href={selectedPartner.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                          {selectedPartner.website}
                        </a>
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-500" /> Contact
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">Name:</span> <strong>{selectedPartner.contact_name}</strong></p>
                    {selectedPartner.contact_title && <p><span className="text-gray-500">Title:</span> {selectedPartner.contact_title}</p>}
                    <p className="flex items-center gap-1"><Mail className="w-4 h-4 text-gray-400" /> {selectedPartner.email}</p>
                    {selectedPartner.phone && <p className="flex items-center gap-1"><Phone className="w-4 h-4 text-gray-400" /> {selectedPartner.phone}</p>}
                    <p className="flex items-center gap-1"><MapPin className="w-4 h-4 text-gray-400" /> {selectedPartner.geographic_reach}</p>
                  </div>
                </div>
              </div>
              
              {/* Partnership Details */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-purple-500" /> Partnership Details
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <p><span className="text-gray-500">Partnership Type:</span> <strong>{selectedPartner.partnership_type}</strong></p>
                  <p><span className="text-gray-500">Geographic Reach:</span> {selectedPartner.geographic_reach}</p>
                </div>
              </div>

              {/* Expertise Areas */}
              {selectedPartner.expertise_areas?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Areas of Expertise</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedPartner.expertise_areas.map((area, i) => (
                      <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Why Partner */}
              {selectedPartner.why_partner && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Why Partner with CNESS?</h4>
                  <p className="text-gray-600 text-sm bg-gray-50 p-4 rounded-xl">{selectedPartner.why_partner}</p>
                </div>
              )}

              {/* Proposed Collaboration */}
              {selectedPartner.proposed_collaboration && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-2">Proposed Collaboration</h4>
                  <p className="text-gray-600 text-sm bg-gray-50 p-4 rounded-xl">{selectedPartner.proposed_collaboration}</p>
                </div>
              )}

              {/* Credentials (if approved) */}
              {selectedPartner.status === 'approved' && selectedPartner.credentials && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-green-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Partner Credentials
                    </h4>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setEditCredentialsData({
                          username: selectedPartner.credentials.username || '',
                          password: ''
                        });
                        setShowEditCredentials(true);
                      }}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      Edit Credentials
                    </Button>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between bg-white p-2 rounded-lg">
                      <span className="text-gray-600">Username: <strong>{selectedPartner.credentials.username}</strong></span>
                      <Button variant="ghost" size="sm" onClick={() => copyCredentials(selectedPartner.credentials.username)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-between bg-white p-2 rounded-lg">
                      <span className="text-gray-600">Password: <strong>{selectedPartner.credentials.password}</strong></span>
                      <Button variant="ghost" size="sm" onClick={() => copyCredentials(selectedPartner.credentials.password)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Timeline */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-500" /> Timeline
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Applied: {formatDate(selectedPartner.applied_at)}</p>
                  {selectedPartner.approved_at && <p>Approved: {formatDate(selectedPartner.approved_at)}</p>}
                  {selectedPartner.rejected_at && <p>Rejected: {formatDate(selectedPartner.rejected_at)}</p>}
                </div>
              </div>
            </CardContent>
            
            {/* Actions */}
            <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
              <div>
                {selectedPartner.status === 'approved' && (
                  <Button 
                    variant="outline"
                    onClick={() => handleToggleStatus(selectedPartner)}
                    className={selectedPartner.is_active !== false ? 'text-red-600 border-red-300' : 'text-green-600 border-green-300'}
                  >
                    {selectedPartner.is_active !== false ? (
                      <><ToggleRight className="w-4 h-4 mr-2" /> Deactivate</>
                    ) : (
                      <><ToggleLeft className="w-4 h-4 mr-2" /> Activate</>
                    )}
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setSelectedPartner(null)}>Close</Button>
                {selectedPartner.status === 'pending' && (
                  <>
                    <Button 
                      variant="destructive"
                      onClick={() => { setApprovalAction('reject'); setShowApprovalModal(true); }}
                    >
                      <XCircle className="w-4 h-4 mr-2" /> Reject
                    </Button>
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => { setApprovalAction('approve'); setShowApprovalModal(true); }}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // Simplified Approval Modal - Just shows partner info and approve button
  const ApprovalModal = () => {
    if (!showApprovalModal || !selectedPartner) return null;

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/60" onClick={() => setShowApprovalModal(false)} />
        
        <Card className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl">
          <CardHeader>
            <CardTitle>
              {approvalAction === 'approve' ? 'Approve Partner Application' : 'Reject Application'}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {approvalAction === 'approve' ? (
              <>
                {/* Partner Summary */}
                <div className="p-4 bg-gray-50 rounded-xl space-y-3">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="text-sm text-gray-500">Organization</p>
                      <p className="font-semibold text-gray-900">{selectedPartner.organization_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="text-sm text-gray-500">Contact Person</p>
                      <p className="font-semibold text-gray-900">{selectedPartner.contact_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="text-sm text-gray-500">Partnership Type</p>
                      <p className="font-semibold text-gray-900">{selectedPartner.partnership_type || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-semibold text-gray-900">{selectedPartner.email}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">
                  <CheckCircle className="w-4 h-4 inline mr-2" />
                  Upon approval, login credentials will be generated and can be edited later.
                </div>
              </>
            ) : (
              <>
                <p className="text-gray-600">
                  Please provide a reason for rejecting this application:
                </p>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Reason for rejection..."
                  className="resize-none"
                  rows={3}
                />
              </>
            )}
          </CardContent>
          
          <div className="p-6 border-t flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowApprovalModal(false)}>Cancel</Button>
            <Button 
              onClick={approvalAction === 'approve' ? handleApprove : handleReject}
              disabled={processing || (approvalAction === 'reject' && !rejectionReason)}
              className={approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={approvalAction === 'reject' ? 'destructive' : 'default'}
            >
              {processing ? 'Processing...' : approvalAction === 'approve' ? 'Approve Partner' : 'Reject'}
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  // Credentials Success Modal
  const CredentialsModal = () => {
    if (!lastApprovedCredentials) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/60" onClick={() => setLastApprovedCredentials(null)} />
        
        <Card className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2">Partner Approved!</h3>
            <p className="text-gray-600 mb-6">
              Credentials generated for <strong>{lastApprovedCredentials.partner_name}</strong>
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Email:</span>
                <span className="font-mono font-medium">{lastApprovedCredentials.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Username:</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-medium">{lastApprovedCredentials.username}</span>
                  <Button variant="ghost" size="sm" onClick={() => copyCredentials(lastApprovedCredentials.username)}>
                    {credentialsCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Password:</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-medium">{lastApprovedCredentials.password}</span>
                  <Button variant="ghost" size="sm" onClick={() => copyCredentials(lastApprovedCredentials.password)}>
                    {credentialsCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700 mb-6">
              MOCKED: Email would be sent to partner with these credentials
            </div>
            
            <Button onClick={() => setLastApprovedCredentials(null)} className="w-full">Done</Button>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Create Partner Modal - Full form same as pre-login
  const CreatePartnerModal = () => {
    const [formData, setFormData] = useState({
      organization_name: '',
      website: '',
      organization_type: '',
      organization_size: '',
      industry_focus: '',
      geographic_reach: '',
      contact_name: '',
      contact_title: '',
      email: '',
      phone: '',
      expertise_areas: [],
      partnership_type: '',
      proposed_collaboration: '',
      relevant_experience: '',
      current_certifications: '',
      why_partner: '',
      expected_outcomes: '',
      references: '',
      additional_info: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const handleInputChange = (e) => {
      setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleExpertiseChange = (expertise) => {
      const updated = formData.expertise_areas.includes(expertise)
        ? formData.expertise_areas.filter(area => area !== expertise)
        : [...formData.expertise_areas, expertise];
      setFormData({ ...formData, expertise_areas: updated });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);

      try {
        const response = await fetch(`${API}/partners/apply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        });
        
        const result = await response.json();
        
        if (response.ok) {
          alert('Partner created successfully!');
          setShowCreateModal(false);
          fetchPartners();
        } else {
          alert(result.detail || 'Error creating partner');
        }
      } catch (error) {
        alert('Error creating partner');
      } finally {
        setSubmitting(false);
      }
    };

    const expertiseOptions = masterData.pillars.length > 0 ? masterData.pillars : PILLAR_NAME_LIST;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCreateModal(false)} />
        
        <div className="relative min-h-screen flex items-center justify-center p-4">
          <Card className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
            <CardHeader className="sticky top-0 bg-white border-b z-10">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-bold">Create New Partner</CardTitle>
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Organization Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-lg">
                    <Building2 className="w-5 h-5 text-purple-500" />
                    Organization Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Organization Name *</Label>
                      <Input name="organization_name" value={formData.organization_name} onChange={handleInputChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input name="website" value={formData.website} onChange={handleInputChange} placeholder="https://" className="mt-1" />
                    </div>
                    <div>
                      <Label>Organization Type *</Label>
                      <select name="organization_type" value={formData.organization_type} onChange={handleInputChange} required className="w-full mt-1 p-2 border rounded-lg">
                        <option value="">Select type</option>
                        {masterData.organization_types.map((type, idx) => (
                          <option key={idx} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Organization Size *</Label>
                      <select name="organization_size" value={formData.organization_size} onChange={handleInputChange} required className="w-full mt-1 p-2 border rounded-lg">
                        <option value="">Select size</option>
                        {masterData.organization_sizes.map((size, idx) => (
                          <option key={idx} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Industry Focus *</Label>
                      <select name="industry_focus" value={formData.industry_focus} onChange={handleInputChange} required className="w-full mt-1 p-2 border rounded-lg">
                        <option value="">Select industry</option>
                        {masterData.industries.map((ind, idx) => (
                          <option key={idx} value={ind}>{ind}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Geographic Reach *</Label>
                      <select name="geographic_reach" value={formData.geographic_reach} onChange={handleInputChange} required className="w-full mt-1 p-2 border rounded-lg">
                        <option value="">Select reach</option>
                        {masterData.geographic_reach.map((reach, idx) => (
                          <option key={idx} value={reach}>{reach}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-purple-500" />
                    Contact Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Contact Name *</Label>
                      <Input name="contact_name" value={formData.contact_name} onChange={handleInputChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label>Contact Title</Label>
                      <Input name="contact_title" value={formData.contact_title} onChange={handleInputChange} className="mt-1" />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input name="email" type="email" value={formData.email} onChange={handleInputChange} required className="mt-1" />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input name="phone" value={formData.phone} onChange={handleInputChange} className="mt-1" />
                    </div>
                  </div>
                </div>

                {/* Areas of Expertise */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-lg">
                    <Award className="w-5 h-5 text-purple-500" />
                    Areas of Expertise (6 Pillars)
                  </h3>
                  <div className="grid md:grid-cols-2 gap-2">
                    {expertiseOptions.map((expertise, idx) => (
                      <label key={idx} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input 
                          type="checkbox"
                          checked={formData.expertise_areas.includes(expertise)}
                          onChange={() => handleExpertiseChange(expertise)}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className="text-sm">{expertise}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Partnership Details */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2 text-lg">
                    <Briefcase className="w-5 h-5 text-purple-500" />
                    Partnership Details
                  </h3>
                  <div>
                    <Label>Partnership Type *</Label>
                    <select name="partnership_type" value={formData.partnership_type} onChange={handleInputChange} required className="w-full mt-1 p-2 border rounded-lg">
                      <option value="">Select type</option>
                      {masterData.partnership_types.map((type, idx) => (
                        <option key={idx} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Proposed Collaboration</Label>
                    <Textarea name="proposed_collaboration" value={formData.proposed_collaboration} onChange={handleInputChange} rows={3} className="mt-1" />
                  </div>
                  <div>
                    <Label>Relevant Experience</Label>
                    <Textarea name="relevant_experience" value={formData.relevant_experience} onChange={handleInputChange} rows={3} className="mt-1" />
                  </div>
                  <div>
                    <Label>Current Certifications</Label>
                    <Input name="current_certifications" value={formData.current_certifications} onChange={handleInputChange} className="mt-1" />
                  </div>
                  <div>
                    <Label>Why Partner with CNESS?</Label>
                    <Textarea name="why_partner" value={formData.why_partner} onChange={handleInputChange} rows={3} className="mt-1" />
                  </div>
                  <div>
                    <Label>Expected Outcomes</Label>
                    <Textarea name="expected_outcomes" value={formData.expected_outcomes} onChange={handleInputChange} rows={3} className="mt-1" />
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 text-lg">Additional Information</h3>
                  <div>
                    <Label>References</Label>
                    <Textarea name="references" value={formData.references} onChange={handleInputChange} rows={2} className="mt-1" />
                  </div>
                  <div>
                    <Label>Additional Information</Label>
                    <Textarea name="additional_info" value={formData.additional_info} onChange={handleInputChange} rows={2} className="mt-1" />
                  </div>
                </div>

                <div className="pt-4 border-t flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4 mr-2" /> Create Partner</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  return (
    <SystemAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Partner Management</h1>
            <p className="text-gray-600 text-sm">Review and manage partner applications</p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            data-testid="create-partner-btn"
          >
            <Plus className="w-5 h-5 mr-2" /> Create New Partner
          </Button>
        </div>
        
        <StatsCards />
        
        {/* Filters */}
        <Card className="mb-6 border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input 
                  placeholder="Search by name, email, or partner ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </CardContent>
        </Card>
        
        {/* Partners Table */}
        <Card className="border-0 shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Partner</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Type</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Tier</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Active</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Applied</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">Loading...</td></tr>
                ) : filteredPartners.length === 0 ? (
                  <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">No partners found</td></tr>
                ) : (
                  filteredPartners.map((partner) => {
                    const statusConfig = STATUS_CONFIG[partner.status] || {};
                    const tierConfig = TIER_CONFIG[partner.tier] || {};
                    
                    return (
                      <tr key={partner.partner_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{partner.organization_name || partner.contact_name}</p>
                            <p className="text-sm text-gray-500">{partner.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 text-sm">
                          {partner.partnership_type || partner.partner_type || '-'}
                        </td>
                        <td className="px-6 py-4">
                          {partner.tier ? (
                            <span 
                              className="px-3 py-1 rounded-full text-xs font-medium"
                              style={{ backgroundColor: tierConfig.bgColor, color: tierConfig.color }}
                            >
                              {tierConfig.name}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <span 
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
                          >
                            {statusConfig.name}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {partner.status === 'approved' && (
                            <button onClick={() => handleToggleStatus(partner)} className="text-gray-500 hover:text-gray-700">
                              {partner.is_active !== false ? (
                                <ToggleRight className="w-6 h-6 text-green-500" />
                              ) : (
                                <ToggleLeft className="w-6 h-6 text-gray-400" />
                              )}
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-500 text-sm">{formatDate(partner.applied_at)}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setSelectedPartner(partner)} data-testid={`view-partner-${partner.partner_id}`}>
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleSharePartner(partner, 'copy')}>
                              <Share2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      
      {selectedPartner && <PartnerDetailModal />}
      {showApprovalModal && <ApprovalModal />}
      {lastApprovedCredentials && <CredentialsModal />}
      {showCreateModal && <CreatePartnerModal />}
      
      {/* Edit Credentials Modal */}
      {showEditCredentials && selectedPartner && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60" onClick={() => setShowEditCredentials(false)} />
          <Card className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
            <CardHeader>
              <CardTitle>Edit Partner Credentials</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Username</Label>
                <Input
                  value={editCredentialsData.username}
                  onChange={(e) => setEditCredentialsData({...editCredentialsData, username: e.target.value})}
                  placeholder="Enter new username"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>New Password</Label>
                <Input
                  type="text"
                  value={editCredentialsData.password}
                  onChange={(e) => setEditCredentialsData({...editCredentialsData, password: e.target.value})}
                  placeholder="Enter new password (leave blank to keep current)"
                  className="mt-1"
                />
              </div>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Partner will need to use these new credentials to login.
              </div>
            </CardContent>
            <div className="p-6 border-t flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowEditCredentials(false)}>Cancel</Button>
              <Button 
                onClick={handleEditCredentials}
                disabled={processing || (!editCredentialsData.username && !editCredentialsData.password)}
              >
                {processing ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </SystemAdminLayout>
  );
};

export default PartnerManagement;
